import { useState, useRef, useEffect } from 'react';
import { MapPin, Linkedin, Instagram } from 'lucide-react';
import { BRAND, PROFILE } from '../constants';
import { DotGrid } from './utils/DotGrid';
import { Typewriter } from './utils/Typewriter';
import { ProjectSlider } from './ProjectSlider';
import { EmailRow } from './EmailRow';

export default function PageAutoral() {
  const [lab] = useState({ mode: 'focus', gain: 1, flare: true, noise: false });
  const mainRef = useRef<HTMLElement | null>(null);

  // viewport assertions para garantir full-bleed (dev only)
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    console.assert(Math.round(rect.width) === window.innerWidth, "[layout] main não está full width");
    console.assert(Math.round(rect.height) >= window.innerHeight - 1, "[layout] main deve ter altura >= viewport");
  }, []);

  // ——— Smoke tests ———
  useEffect(() => {
    const tests: Array<[string, boolean]> = [];
    const sidebar = document.querySelector('[data-testid="sidebar-panel"]') as HTMLElement | null;
    const content = document.querySelector('[data-testid="content-panel"]') as HTMLElement | null;
    const hero = document.querySelector('[data-testid="identity-hero"]') as HTMLElement | null;
    const innerHero = document.querySelector('[data-testid="identity-hero-inner"]') as HTMLElement | null;
    tests.push(["layout-root existe", !!mainRef.current]);
    tests.push(["sidebar existe", !!sidebar]);
    tests.push(["content existe", !!content]);
    // sidebar com fundo #0A0A0A e sem inline background
    if (sidebar) {
      const bg = getComputedStyle(sidebar).backgroundColor || "";
      tests.push(["sidebar bg #0A0A0A", bg.includes("10, 10, 10")]);
      tests.push(["sidebar sem background inline", !(sidebar.getAttribute("style") || "").includes("background")]);
      const hasGlow = !!sidebar.querySelector('.blur-2xl');
      tests.push(["sidebar sem glow", !hasGlow]);
      const wordmark = sidebar.querySelector('[data-testid="wordmark"]') as HTMLElement | null;
      const tmEl = sidebar.querySelector('[data-testid="tm"]') as HTMLElement | null;
      tests.push(["wordmark presente", !!wordmark]);
      const wmText = wordmark ? (wordmark.textContent || '').replace('®', '').trim() : '';
      tests.push(["wordmark = 'hugo esquerdo' (minúsculo)", wmText.toLowerCase() === 'hugo esquerdo']);
      tests.push(["trademark ® presente", !!tmEl && (tmEl.textContent || '') === '®']);
      const avatar = sidebar.querySelector('[data-testid="avatar"]') as HTMLElement | null;
      tests.push(["avatar pixel presente", !!avatar]);
      const typed = sidebar.querySelector('[data-testid="typed-heading"]') as HTMLElement | null;
      tests.push(["typed heading existe", !!typed]);
      if (typed) {
        const hSpan = typed.querySelector('span');
        if (hSpan) {
          const fs = parseFloat(getComputedStyle(hSpan as HTMLElement).fontSize || '0');
          tests.push(["typed font-size ≤ 64px", fs <= 64]);
        }
        const caret = typed.querySelector('[data-testid="type-caret"]');
        tests.push(["typed usa underscore _", !!caret && (caret.textContent || '') === '_']);
        const expected = (typed.getAttribute('data-expected') || 'Takedown and Internet Specialist');
        setTimeout(() => {
          const txt = (typed.textContent || '').trim();
          console.log(`[test later] typed completo: ${txt.includes(expected) ? 'PASS' : 'FAIL'}`);
        }, 5000);
      }
      const bioEl = sidebar.querySelector('p.mt-4') as HTMLElement | null;
      const bioText = bioEl ? (bioEl.textContent || '').toLowerCase() : '';
      tests.push(["bio em inglês (contains 'malicious')", bioText.includes('malicious')]);
      tests.push(["bio menciona 'infringing' ou 'takedown'", bioText.includes('infringing') || bioText.includes('takedown')]);
      tests.push(["bio começa com 'dedicated to taking down'", bioText.trim().startsWith('dedicated to taking down')]);
      const bioRaw = bioEl ? (bioEl.textContent || '') : '';
      tests.push(["bio tem emoji 😎", bioRaw.includes('😎')]);
      tests.push(["emoji no final da bio", bioRaw.trim().endsWith('😎')]);
      const locEl = sidebar.querySelector('[data-testid="location"]') as HTMLElement | null;
      tests.push(["location presente", !!locEl]);
      tests.push(["location contém 'Volta Redonda - RJ'", !!locEl && (locEl.textContent || '').includes('Volta Redonda - RJ')]);
      if (locEl) {
        const fsLoc = parseFloat(getComputedStyle(locEl).fontSize || '0');
        tests.push(["location font-size ≥ 16px", fsLoc >= 16]);
        const colLoc = getComputedStyle(locEl).color || '';
        tests.push(["location opacity ≥ .75", colLoc.includes(', 0.8') || colLoc.includes(', 1)')]);
      }
      const ig = sidebar.querySelector('[aria-label="Instagram"]');
      const li = sidebar.querySelector('[aria-label="LinkedIn"]');
      const gh = sidebar.querySelector('[aria-label="GitHub"]');
      const tel = sidebar.querySelector('a[href^="tel:"]');
      tests.push(["tem LinkedIn", !!li]);
      tests.push(["tem Instagram", !!ig]);
      tests.push(["sem GitHub", !gh]);
      tests.push(["sem telefone", !tel]);
      
      const cta = sidebar.querySelector('a[href^="mailto:"]') as HTMLElement | null;
      if (locEl && cta) {
        tests.push(["location acima do CTA", locEl.getBoundingClientRect().top < cta.getBoundingClientRect().top]);
        const sidebarRect = sidebar.getBoundingClientRect();
        const ctaRect = cta.getBoundingClientRect();
        tests.push(["CTA no final do card (<=32px do fundo)", sidebarRect.bottom - ctaRect.bottom <= 32]);
        const bgCTA = getComputedStyle(cta).backgroundColor || '';
        tests.push(["CTA sem background (texto)", bgCTA === 'rgba(0, 0, 0, 0)' || bgCTA === 'transparent']);
        tests.push(["CTA exibe email correto", (cta.textContent || '').includes('contact@hugoesquerdo.com')]);
        const copyBtn = sidebar.querySelector('button[aria-label="Copy email"], button[aria-label="Copied"]');
        tests.push(["botão copiar email presente", !!copyBtn]);
        const svg = cta.querySelector('svg');
        tests.push(["CTA tem ícone SVG", !!svg]);
      }
      const cvLink = sidebar.querySelector('a[href="/curriculo.pdf"]');
      tests.push(["sem Baixar CV", !cvLink]);
      const companiesTitlePresent = (sidebar.textContent || '').includes('Empresas com quem trabalhei');
      tests.push(["sem seção empresas", !companiesTitlePresent]);
    }
    if (window.innerWidth >= 768 && sidebar) {
      const sw = sidebar.getBoundingClientRect().width;
      tests.push(["sidebar >= 420px em md+", sw >= 419]);
    }
    tests.push(["sem scroll horizontal", document.documentElement.scrollWidth <= window.innerWidth]);
    if (hero && content) {
      const hr = hero.getBoundingClientRect();
      const ct = content.getBoundingClientRect();
      tests.push(["hero cobre altura do card", Math.abs(hr.height - ct.height) <= 2]);
      tests.push(["hero cobre largura do card", Math.abs(hr.width - ct.width) <= 2]);
      if (innerHero) {
        const innerBg = getComputedStyle(innerHero).backgroundColor || "";
        tests.push(["hero inner bg #0A0A0A", innerBg.includes("10, 10, 10")]);
      }
      const bw = parseInt(getComputedStyle(hero).borderTopWidth || '0', 10);
      const bgImg = getComputedStyle(hero).backgroundImage || '';
      tests.push(["borda gradient ≥ 4px", bw >= 4]);
      tests.push(["borda usa #3F30F1 (camadas)", bgImg.includes('conic-gradient') && (bgImg.includes('63, 48, 241') || bgImg.includes('#3F30F1'))]);
      const hasOuterGlow = !!hero.querySelector('[style*="0 0 30px"]');
      tests.push(["sem outer glow", !hasOuterGlow]);
      const root = document.querySelector('[data-testid="layout-root"]') as HTMLElement | null;
      tests.push(["bg do projeto #100c0c", !!root && root.className.includes('bg-[#100c0c]')]);

      const prevBtn = content.querySelector('button[aria-label="Previous"]');
      const nextBtn = content.querySelector('button[aria-label="Next"]');
      tests.push(["slider tem Previous", !!prevBtn]);
      tests.push(["slider tem Next", !!nextBtn]);
      const firstTitlePresent = (content.textContent || '').includes('Cosmic Navigator');
      tests.push(["primeiro slide 'Cosmic Navigator' visível (texto)", firstTitlePresent]);
      const titleEl = Array.from(content.querySelectorAll('h3')).find(el => (el.textContent || '').includes('Cosmic Navigator')) as HTMLElement | null;
      tests.push(["título usa Apple font inline", !!titleEl && ((titleEl.getAttribute('style') || '').toLowerCase().includes('sf pro'))]);
      if (titleEl) {
        const fsTitle = parseFloat(getComputedStyle(titleEl).fontSize || '0');
        tests.push(["título ≥ 28px", fsTitle >= 28]);
        const fw = parseInt(getComputedStyle(titleEl).fontWeight || '0', 10);
        tests.push(["título font-weight ≥ 800", fw >= 800]);
      }

      const starCanvas = content.querySelector('canvas');
      tests.push(["starfield canvas presente", !!starCanvas]);
    } else {
      tests.push(["hero existe", !!hero]);
    }
    tests.forEach(([name, ok]) => console.log(`[test] ${name}: ${ok ? 'PASS' : 'FAIL'}`));
  }, []);

  return (
    <main
      ref={mainRef}
      data-testid="layout-root"
      className="h-dvh w-full overflow-hidden bg-[#0d0d0d] text-[15px] text-white"

    >
      <div className="h-full w-full p-3 md:p-4">
        <div className="grid h-full w-full grid-cols-1 gap-3 md:grid-cols-[420px_minmax(0,1fr)] lg:grid-cols-[480px_minmax(0,1fr)] md:gap-4">
          {/* ——— sidebar ——— */}
          <aside className="relative overflow-hidden rounded-2xl border border-white/10 bg-black p-6 md:sticky md:top-0" data-testid="sidebar-panel">
            <DotGrid color="rgba(234,234,234,0.08)" size={14} opacity={1} dot={1.2} />
            <div className="relative flex min-h-full flex-col">
              <div className="mb-6 flex items-center gap-3">
                <img
                  data-testid="avatar"
                  src={PROFILE.avatar}
                  alt="Pixel portrait of Hugo"
                  className="h-10 w-10 rounded-lg border border-white/10 bg-black/20 object-cover"
                  style={{ imageRendering: 'pixelated' }}
                />
                <div
                  data-testid="wordmark"
                  className="text-2xl font-extrabold tracking-tight text-white"
                  style={{
                    fontFamily:
                      '"SF Pro Display", "SF Pro Text", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, "Segoe UI", Roboto, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                  }}
                >
                  hugo esquerdo
                  <span
                    data-testid="tm"
                    className="ml-1 font-black opacity-80"
                    style={{ verticalAlign: 'super', fontSize: '0.65em', lineHeight: 1 }}
                    aria-label="registered trademark"
                  >
                    ®
                  </span>
                </div>
              </div>

              <div
                data-testid="typed-heading"
                data-expected="Takedown and Internet Specialist"
                className="mt-6 text-white"
                style={{
                  fontFamily:
                    '"SF Pro Display", "SF Pro Text", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, "Segoe UI", Roboto, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                }}
              >
                <Typewriter
                  text="Takedown and Internet Specialist"
                  speed={40}
                  startDelay={150}
                  loop={false}
                  className="block text-4xl md:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight break-words"
                  cursor
                />
              </div>

              <p className="mt-8 max-w-prose whitespace-pre-line text-base md:text-lg lg:text-xl text-white/80 leading-relaxed">{PROFILE.bio}</p>

              <div data-testid="location" className="mt-6 inline-flex items-center gap-2 text-base text-white/80">
                <MapPin className="h-5 w-5" /> {PROFILE.location}
              </div>

              <div className="mt-8 flex items-center gap-3 text-white/60">
                <a href={PROFILE.contacts.linkedin} className="rounded-full border border-white/15 bg-white/5 p-2 hover:bg-white/10" aria-label="LinkedIn">
                  <Linkedin className="h-4 w-4" />
                </a>
                <a href={PROFILE.contacts.instagram} className="rounded-full border border-white/15 bg-white/5 p-2 hover:bg-white/10" aria-label="Instagram">
                  <Instagram className="h-4 w-4" />
                </a>
              </div>

              <EmailRow email={PROFILE.contacts.email} />
            </div>
          </aside>

          {/* ——— conteúdo principal ——— */}
          <section className="relative h-full overflow-hidden p-0" data-testid="content-panel">
            <div className="h-full min-h-0">
              <ProjectSlider lab={lab} />
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
